import React, { useEffect, useState } from 'react';
import { Stack, Text } from '@chakra-ui/layout';
import Chart from 'react-google-charts';
import { HStack, Spinner } from '@chakra-ui/react';
import { Card } from '~/components/Card';
import { useDolarChart } from '~/hooks/useStrategicMap';
import LoadingDelay from '~/pages/Portal/StrategicMap/CDIChart';
import { formatCurrency } from '~/utils/formatCurrency';

export const DolarChart: React.FC<{
  id_contrato: number;
  data_inicio: string;
  dolar_key: string;
}> = ({ id_contrato, data_inicio, dolar_key }) => {
  const {
    data: dataChart,
    isLoading: isLoadingChart,
    isError: isErrorChart,
    isFetching: isFetchingChart,
  } = useDolarChart(id_contrato, data_inicio);

  const circle = (color: string) => {
    return (
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="5" cy="5" r="5" fill={color} />
      </svg>
    );
  };

  const header = () => {
    return (
      <HStack
        w="60vw"
        left="8px"
        position="sticky"
        justifyContent="space-between"
      >
        <HStack gridGap={2}>
          <Text fontWeight="bold">Dólar -</Text>
          {!isLoadingChart && !!dataChart?.dolar_hoje ? (
            <Text>
              {dataChart?.dolar_hoje.data}: &ensp; $1 -{' '}
              {formatCurrency(Number(dataChart?.dolar_hoje.valor))}
            </Text>
          ) : isErrorChart ? (
            <Text>Falha ao carregar os valores de dólar para os períodos.</Text>
          ) : (
            <Spinner />
          )}
        </HStack>
        <HStack gridGap={4}>
          <Text display="flex" alignItems="center" gridGap={2}>
            {circle('#1BA65B')} Início do Contrato
          </Text>
          <Text display="flex" alignItems="center" gridGap={2}>
            {circle('#FC8E0E')} Contrato atual
          </Text>
        </HStack>
      </HStack>
    );
  };

  return (
    <Card
      bg="transparent"
      marginTop="10px"
      width="100%"
      minW="100%"
      header={header()}
      key={dolar_key}
      content={
        <HStack>
          <Stack minW="50%" w="50%">
            <LoadingDelay delay={4000} fallback={<Spinner />}>
              <Chart
                width="99%"
                height="300px"
                chartType="AreaChart"
                data={dataChart?.grafico_anterior}
                key={dolar_key}
                chartLanguage="pt-BR"
                options={{
                  pointsVisible: true,
                  curveType: 'function',
                  series: [{ color: '#70c2ff' }],
                  intervals: { style: 'area' },
                  legend: { position: 'left' },
                  chartArea: {
                    left: 50,
                    top: 20,
                    bottom: 50,
                    width: '85%',
                    height: '70%',
                  },
                  hAxis: {
                    title: 'Últimos 6 meses antes do início do contrato',
                    minValue: 0,
                    textStyle: {
                      fontSize: 10,
                    },
                    titleTextStyle: {
                      bold: true,
                    },
                  },
                  vAxis: {
                    textStyle: {
                      fontSize: 10,
                      color: '#000000',
                    },
                    minValue: 0,
                    format: '####',
                  },
                  annotations: {
                    textStyle: {
                      fontSize: 10,
                      color: '#000',
                      auraColor: 'none',
                    },
                  },
                  tooltip: { isHtml: true },
                }}
                formatters={[
                  {
                    type: 'NumberFormat',
                    column: 1,
                    options: {
                      decimalSymbol: ',',
                      groupingSymbol: '.',
                      fractionDigits: 0,
                    },
                  },
                ]}
              />
            </LoadingDelay>
          </Stack>
          <Stack minW="50%" w="50%">
            <LoadingDelay delay={4000} fallback={<Spinner />}>
              <Chart
                width="99%"
                height="300px"
                chartType="AreaChart"
                data={dataChart?.grafico_atual}
                key={dolar_key}
                chartLanguage="pt-BR"
                options={{
                  pointsVisible: true,
                  curveType: 'function',
                  series: [{ color: '#ffb850' }],
                  intervals: { style: 'area' },
                  legend: { position: 'left' },
                  chartArea: {
                    left: 50,
                    top: 20,
                    bottom: 50,
                    width: '85%',
                    height: '70%',
                  },
                  hAxis: {
                    title: 'Últimos 6 meses do contrato',
                    minValue: 0,
                    textStyle: {
                      fontSize: 10,
                    },
                    titleTextStyle: {
                      bold: true,
                    },
                  },
                  vAxis: {
                    textStyle: {
                      fontSize: 10,
                      color: '#000000',
                    },
                    minValue: 0,
                    format: '####',
                  },
                  annotations: {
                    textStyle: {
                      fontSize: 10,
                      color: '#000',
                      auraColor: 'none',
                    },
                  },
                  tooltip: { isHtml: true },
                }}
                formatters={[
                  {
                    type: 'NumberFormat',
                    column: 1,
                    options: {
                      decimalSymbol: ',',
                      groupingSymbol: '.',
                      fractionDigits: 0,
                    },
                  },
                ]}
              />
            </LoadingDelay>
          </Stack>
        </HStack>
      }
    />
  );
};
